import { useDataGrid, List, ShowButton, EditButton } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Typography, CircularProgress, Link, Button } from '@mui/material'

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import InfoIcon from '@mui/icons-material/Info'
import { useGetIdentity, useOne } from '@refinedev/core'
import { User } from '@/utils/auth/auth0'

export default function UserSpecificFundersList() {
  const user = useGetIdentity().data as User

  const { data: userData, isLoading } = useOne({
    resource: 'user-by-email',
    id: user?.email,
    queryOptions: {
      enabled: !!user,
    },
  })

  const { dataGridProps } = useDataGrid({
    resource: 'assigned-funders',
    meta: {
      user_uuid: userData,
    },
    queryOptions: {
      enabled: !!userData,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting assigned funders',
        description:
          error?.message || 'An error occurred while fetching assigned funders',
        type: 'error',
      }
    },
  })

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'opps_status',
      headerName: 'Opps Status',
      flex: 0.35,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: function render({ row }) {
        if (!row.opps_status) return null
        const color =
          row.opps_status === 'green'
            ? '#4caf50'
            : row.opps_status === 'yellow'
              ? '#ff9800'
              : row.opps_status === 'red'
                ? '#f44336'
                : '#9e9e9e'
        return <FiberManualRecordIcon sx={{ fontSize: '1.2rem', color }} />
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      flex: 0.25,
      minWidth: 75,
      align: 'center',
      headerAlign: 'center',
      renderCell: function render({ row }) {
        return (
          <FiberManualRecordIcon
            sx={{
              fontSize: '1.2rem',
              color: row.active ? '#4caf50' : '#f44336',
            }}
          />
        )
      },
    },
    {
      field: 'next_check_in_date',
      headerName: 'Next Check-in',
      sortable: true,
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.next_check_in_date
              ? new Date(row.next_check_in_date).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        )
      },
      minWidth: 120,
      align: 'center',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: function render({ row }) {
        return (
          <>
            <ShowButton hideText resource="funders" recordItemId={row.uuid} />
            <EditButton hideText resource="funders" recordItemId={row.uuid} />
          </>
        )
      },

      maxWidth: 100,
    },
  ]

  if (isLoading) {
    return <CircularProgress size={20} />
  }

  return (
    <>
      <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
        {user?.email && userData ? (
          <Button variant="contained" sx={{ mb: 2, color: 'white' }}>
            <Link href={`/funders`} style={{ color: 'white' }}>
              Back to Full List
            </Link>
          </Button>
        ) : (
          'Loading...'
        )}
      </Typography>
      <List>
        <Typography variant="h6">Assigned Funders</Typography>
        <DataGrid
          {...dataGridProps}
          columns={columns}
          autoHeight
          getRowId={(row) => row.uuid}
        />
      </List>
    </>
  )
}
